import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Onboarding from './Onboarding';

// Composants modulaires
import Dashboard from './components/Dashboard';
import { LoginForm } from './components/Auth';
import ClientsList from './components/Clients';
import ProductsList from './components/Produits';
import Comptabilite from './components/Comptabilite/Comptabilite';
import CommandesList from './components/Commandes/CommandesList';
import Statistiques from './components/Statistiques/Statistiques';
import Reglages from './components/Reglages/Reglages';
import UsersList from './components/Users/UsersList';
import { AppProvider } from './contexts/AppContext';
import { formatNumber, formatCurrency, getCurrencySymbol, formatDate, translateStatut, adjustColor } from './utils/formatters';
import { cacheGet, cacheSet, cacheClearAll } from './utils/cache';
import { parseDecimal } from './utils/parseDecimal';
import { Eye, Truck, FileText, FileX2, Pencil, Trash2, History, PauseCircle, Play, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8001`;
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || 'Votre Entreprise';

function App() {
  // Fonctions de vérification des rôles
  const isAdmin = () => currentUser?.role === 'admin';
  const isComptable = () => currentUser?.role === 'comptable';
  const hasWriteAccess = () => currentUser?.role !== 'comptable';

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Récupérer l'onglet depuis localStorage, sinon 'dashboard' par défaut
    return localStorage.getItem('activeTab') || 'dashboard';
  });
  const [clients, setClients] = useState(() => cacheGet('clients', []));
  // filteredClients supprimé - le filtrage se fait dans getFilteredClients()
  const [clientTypeFilter, setClientTypeFilter] = useState('');
  const [clientCaFilter, setClientCaFilter] = useState('');

  // États pour la recherche
  const [searchTermClients, setSearchTermClients] = useState('');
  const [searchTermProduits, setSearchTermProduits] = useState('');
  const [produits, setProduits] = useState(() => cacheGet('produits', []));
  const [filteredProduits, setFilteredProduits] = useState([]);
  const [produitFamilleFilter, setProduitFamilleFilter] = useState('');
  const [commandeClientFilter, setCommandeClientFilter] = useState('');
  const [commandeSearchTerm, setCommandeSearchTerm] = useState('');
  const [commandes, setCommandes] = useState(() => cacheGet('commandes', []));
  const [users, setUsers] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [showStockHistory, setShowStockHistory] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [showStockForm, setShowStockForm] = useState(false);

  // États pour pagination
  const [currentPageClients, setCurrentPageClients] = useState(1);
  const [currentPageProduits, setCurrentPageProduits] = useState(1);
  const [currentPageCommandes, setCurrentPageCommandes] = useState(1);
  const [currentPageUsers, setCurrentPageUsers] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [statistiques, setStatistiques] = useState(() => cacheGet('statistiques', null));
  const [statistiquesProduits, setStatistiquesProduits] = useState(() => cacheGet('statistiquesProduits', null));
  const [statistiquesOrigines, setStatistiquesOrigines] = useState(() => cacheGet('statistiquesOrigines', null));
  const [settings, setSettings] = useState(null);
  const [gestionStockActive, setGestionStockActive] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  
  // États pour les formulaires
  const [showLoginForm, setShowLoginForm] = useState(true);
  // Note: showForgotPasswordForm, showResetPasswordForm et resetToken sont maintenant gérés dans LoginForm
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  const [clientOrderHistory, setClientOrderHistory] = useState([]);
  const [clientProductStats, setClientProductStats] = useState([]);
  const [loadingClientHistory, setLoadingClientHistory] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('general');
  const [exportType, setExportType] = useState('period');
  const [showProduitForm, setShowProduitForm] = useState(false);
  const [showCommandeForm, setShowCommandeForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingProduit, setEditingProduit] = useState(null);
  const [editingCommande, setEditingCommande] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [nbCommandesAffichees, setNbCommandesAffichees] = useState(5);
  
  // États pour les produits
  const [showFinDeSaison, setShowFinDeSaison] = useState(false);
  const [familleFilter, setFamilleFilter] = useState('');
  
  // États pour l'onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  
  // État pour les lignes de produits du formulaire commande
  const [lignesProduits, setLignesProduits] = useState([
    { id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '', update_prix: false }
  ]);

  // Initialiser les lignes de produits quand on édite une commande
  useEffect(() => {
    if (showCommandeForm && editingCommande) {
      // Charger les produits de la commande en cours d'édition.
      // 🔑 Toujours pré-remplir prix_personnalise avec le prix réellement utilisé sur la commande
      // (avant le fix, le champ pouvait être vide quand le prix correspondait au prix par défaut
      // d'origine, masquant ainsi la valeur saisie initialement).
      const lignes = editingCommande.produits.map(p => ({
        id: Date.now() + Math.random(),
        produit_id: p.produit_id,
        quantite: p.quantite,
        prix_personnalise: p.prix_unitaire,
        prix_defaut: p.prix_defaut || p.prix_unitaire,
        update_prix: false
      }));
      setLignesProduits(lignes);
    } else if (showCommandeForm && !editingCommande) {
      // Réinitialiser pour une nouvelle commande
      setLignesProduits([{ id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '', update_prix: false }]);
    }
  }, [showCommandeForm, editingCommande]);

  // Charger les données spécifiques à l'onglet actif
  useEffect(() => {
    if (activeTab === 'users' && currentUser?.role === 'admin') {
      loadUsers();
    }
  }, [activeTab, currentUser]);

  // Charger les paramètres (notamment la couleur) au démarrage de l'application
  useEffect(() => {
    loadSettings(); // Charger immédiatement pour le thème de la page de login
  }, []);

  // 🖱️ Désactiver le scroll de la souris sur les inputs number (évite les changements
  // accidentels de valeurs lors de la création de produits/commandes).
  // On blur l'input dès qu'on scrolle dessus → la valeur n'est plus modifiée et la page
  // continue de défiler normalement.
  useEffect(() => {
    const handleWheel = (e) => {
      const target = e.target;
      if (target instanceof HTMLInputElement && target.type === 'number') {
        target.blur();
      }
    };
    document.addEventListener('wheel', handleWheel);
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);

  // Vérifier la connexion au démarrage
  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      loadClients();
      loadProduits();
      loadCommandes();
      loadSettings(); // Recharger au cas où il y aurait des mises à jour
      loadStockAlerts();
      if (currentUser?.role === 'admin') {
        loadUsers();
      }
      if (activeTab === 'statistiques') {
        loadStatistiques();
        loadStatistiquesProduits();
        loadStatistiquesOrigines();
      }
    }
  }, [isLoggedIn, currentUser, activeTab]);

  // Réinitialiser la pagination quand le filtre client change
  useEffect(() => {
    setCurrentPageCommandes(1);
  }, [commandeClientFilter]);

  // Fonction pour afficher une notification toast
  const showNotification = (message, type = 'success') => {
    const id = Date.now();
    const newNotification = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      timestamp: new Date()
    };
    
    setNotifications(prev => [...prev, newNotification]);
    
    // Supprimer automatiquement après quelques secondes
    // (les erreurs / warnings multi-lignes restent visibles plus longtemps)
    const duration = (type === 'error' || type === 'warning') && String(message).includes('\n') ? 12000 : 5000;
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);
  };

  // Fonction pour supprimer une notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Fonction pour rafraîchir tout le tableau de bord
  const refreshDashboard = async () => {
    try {
      await Promise.all([
        loadCommandes(),
        loadStatistiques(),
        loadProduits(), // Pour les stocks mis à jour
        loadStockAlerts() // Pour les nouvelles alertes
      ]);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du tableau de bord:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'  // 🔒 SÉCURITÉ : Envoyer les cookies pour l'authentification
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setGestionStockActive(data.gestion_stock_active !== false); // Mettre à jour l'état local
        
        // Appliquer les couleurs dynamiquement
        if (data.couleur_primaire) {
          // Sauvegarder dans localStorage pour chargement instantané au prochain refresh
          localStorage.setItem('app_primary_color', data.couleur_primaire);
          
          document.documentElement.style.setProperty('--primary-color', data.couleur_primaire);
          document.documentElement.style.setProperty('--primary-dark', adjustColor(data.couleur_primaire, -20));
          document.documentElement.style.setProperty('--primary-light', adjustColor(data.couleur_primaire, 20));
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  };

  // Fonction pour ajuster la luminosité d'une couleur
  const adjustColor = (color, amount) => {
    const usePound = color[0] === '#';
    const col = usePound ? color.slice(1) : color;
    const num = parseInt(col, 16);
    let r = (num >> 16) + amount;
    let g = (num >> 8 & 0x00FF) + amount;
    let b = (num & 0x0000FF) + amount;
    r = r > 255 ? 255 : r < 0 ? 0 : r;
    g = g > 255 ? 255 : g < 0 ? 0 : g;
    b = b > 255 ? 255 : b < 0 ? 0 : b;
    return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
  };

  const checkAuth = async () => {
    try {
      // 🔒 SÉCURITÉ : Vérifier l'authentification via le cookie HttpOnly
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'  // Envoie automatiquement le cookie
      });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        setIsLoggedIn(true);
        setShowLoginForm(false);
        
        // Vérifier si l'onboarding doit être affiché
        if (!userData.onboarding_completed && !userData.onboarding_skipped) {
          setOnboardingCompleted(false);
        } else {
          setOnboardingCompleted(true);
        }
      } else {
        // Non authentifié - afficher le formulaire de connexion
        setIsLoggedIn(false);
        setShowLoginForm(true);
      }
    } catch (error) {
      console.error('Erreur de vérification:', error);
      setIsLoggedIn(false);
      setShowLoginForm(true);
    }
  };

  const handleForgotPassword = async (email) => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        body: JSON.stringify({ email: email })
      });

      if (response.ok) {
        showNotification('Email de réinitialisation envoyé si le compte existe', 'success');
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la demande de réinitialisation', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la demande de réinitialisation:', error);
      showNotification('Erreur lors de la demande de réinitialisation', 'error');
    }
  };

  const handleResetPassword = async (token, newPassword, confirmPassword) => {
    if (newPassword !== confirmPassword) {
      showNotification('Les mots de passe ne correspondent pas', 'error');
      return;
    }

    if (newPassword.length < 8) {
      showNotification('Le mot de passe doit contenir au moins 8 caractères', 'error');
      return;
    }

    try {
      const response = await apiCall(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ token: token, new_password: newPassword })
      });

      if (response.ok) {
        showNotification('Mot de passe réinitialisé avec succès', 'success');
        setShowLoginForm(true);
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la réinitialisation', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      showNotification('Erreur lors de la réinitialisation', 'error');
    }
  };

  // 🔒 Fonction pour rafraîchir le token
  const refreshAccessToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        console.log('🔄 Token rafraîchi avec succès');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur lors du refresh token:', error);
      return false;
    }
  };

  // Variable pour éviter les refresh multiples simultanés
  let isRefreshing = false;
  let refreshPromise = null;

  const apiCall = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    let response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });
    
    // Si 401 et pas déjà en train de refresh, essayer de refresh le token
    if (response.status === 401 && !url.includes('/api/auth/refresh') && !url.includes('/api/auth/login')) {
      // Éviter les refresh multiples simultanés
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken();
      }
      
      const refreshed = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;
      
      if (refreshed) {
        // Rejouer la requête originale
        response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include'
        });
      } else {
        // Refresh échoué, déconnecter l'utilisateur
        console.log('🔒 Session expirée, déconnexion...');
        setIsLoggedIn(false);
        setCurrentUser(null);
        setShowLoginForm(true);
      }
    }
    
    return response;
  };

  // 🔒 Fonction API avec refresh automatique (alias pour compatibilité)
  const apiCallWithRefresh = apiCall;

  const handleLogin = async (username, password) => {
    const loginData = { username, password };

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginData)
      });

      if (response.ok) {
        const data = await response.json();

        // 🏠 Toujours arriver sur le tableau de bord lors d'une nouvelle connexion
        localStorage.setItem('activeTab', 'dashboard');
        setActiveTab('dashboard');

        setCurrentUser(data.user);
        setIsLoggedIn(true);
        setShowLoginForm(false);
        setLoginError('');
        
        if (data.needs_password_change) {
          setNeedsPasswordChange(true);
          setShowPasswordChangeModal(true);
        }
      } else {
        const errorData = await response.json();
        
        // 🔒 Gestion spéciale pour le rate limiting (code 429)
        if (response.status === 429) {
          setLoginError(errorData.detail || 'Trop de tentatives. Veuillez patienter.');
        } else {
          setLoginError(errorData.detail || 'Erreur de connexion');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      setLoginError('Erreur de connexion au serveur');
    }
  };

  const handleLogout = async () => {
    try {
      // 🔒 SÉCURITÉ : Appeler le endpoint logout pour supprimer le cookie HttpOnly
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
    
    // Réinitialiser l'état local
    setIsLoggedIn(false);
    setCurrentUser(null);
    setShowLoginForm(true);
    setActiveTab('dashboard');
    localStorage.setItem('activeTab', 'dashboard');
    setIsMobileMenuOpen(false);
    // Purger le cache pour ne pas exposer les données de l'utilisateur précédent
    cacheClearAll();
  };

  // NOUVEAU: Gestion du scroll pour back-to-top
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // NOUVEAU: Fonction back-to-top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Effect pour gérer la fermeture du modal client avec Echap
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showClientDetail) {
        closeClientDetail();
      }
    };

    if (showClientDetail) {
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('keydown', handleEscKey);
        // Nettoyer le style overflow au cas où
        document.body.style.overflow = 'unset';
      };
    }
  }, [showClientDetail]);

  // NOUVEAU: Fermer les menus sur clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileUserMenuOpen && !event.target.closest('.header-actions')) {
        setMobileUserMenuOpen(false);
      }
      // Fermer le menu mobile seulement si on clique en dehors de la navigation ET du bouton toggle
      if (isMobileMenuOpen && 
          !event.target.closest('.nav-container') && 
          !event.target.closest('.mobile-menu-toggle')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileUserMenuOpen, isMobileMenuOpen]);

  const loadClients = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/clients`);
      if (response.ok) {
        const data = await response.json();
        setClients(data);
        cacheSet('clients', data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  const loadProduits = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/produits`);
      if (response.ok) {
        const data = await response.json();
        // Trier les produits par ordre alphabétique (nom)
        const produitsTriés = data.sort((a, b) => a.nom.localeCompare(b.nom));
        setProduits(produitsTriés);
        cacheSet('produits', produitsTriés);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    }
  };

  const loadCommandes = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/commandes`);
      if (response.ok) {
        const data = await response.json();
        // Trier les commandes par date de création (plus récente en premier)
        const commandesTriees = data.sort((a, b) => {
          const dateA = new Date(a.date_commande || 0);
          const dateB = new Date(b.date_commande || 0);
          return dateB - dateA; // Ordre décroissant (plus récent en premier)
        });
        setCommandes(commandesTriees);
        cacheSet('commandes', commandesTriees);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  const loadStatistiques = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/statistiques`);
      if (response.ok) {
        const data = await response.json();
        setStatistiques(data);
        cacheSet('statistiques', data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const loadStatistiquesProduits = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/statistiques-produits`);
      if (response.ok) {
        const data = await response.json();
        setStatistiquesProduits(data);
        cacheSet('statistiquesProduits', data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques produits:', error);
    }
  };

  const loadStatistiquesOrigines = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/statistiques-origines`);
      if (response.ok) {
        const data = await response.json();
        setStatistiquesOrigines(data);
        cacheSet('statistiquesOrigines', data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques par origine:', error);
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const clientData = {
      nom_entreprise: formData.get('nom_entreprise'),
      siret: formData.get('siret'),
      adresse: formData.get('adresse'),
      telephone: formData.get('telephone'),
      email: formData.get('email'),
      type_client: formData.get('type_client')
    };

    try {
      const url = editingClient ? `${API_URL}/api/clients/${editingClient.id}` : `${API_URL}/api/clients`;
      const method = editingClient ? 'PUT' : 'POST';
      
      const response = await apiCall(url, {
        method: method,
        body: JSON.stringify(clientData)
      });

      if (response.ok) {
        loadClients();
        setShowClientForm(false);
        setEditingClient(null);
        showNotification(editingClient ? 'Client modifié avec succès' : 'Client créé avec succès', 'success');
      } else {
        showNotification('Erreur lors de la sauvegarde du client', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du client:', error);
      showNotification('Erreur lors de la sauvegarde du client', 'error');
    }
  };

  const handleProduitSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // 🛡️ Helper : convertit en nombre, accepte virgule ET point comme séparateur.
    // Retourne null si invalide (évite NaN qui casse Pydantic).
    const toNumOrNull = parseDecimal;
    const prixUnitaire = toNumOrNull(formData.get('prix_unitaire'));
    if (prixUnitaire === null) {
      showNotification('Le prix unitaire est obligatoire', 'error');
      return;
    }

    const produitData = {
      nom: formData.get('nom'),
      description: formData.get('description') || '',
      famille: formData.get('famille') || null,
      variete: formData.get('variete') || null,
      bio: formData.get('bio') === 'on',
      fin_de_saison: formData.get('fin_de_saison') === 'on',
      prix_unitaire: prixUnitaire,
      unite: formData.get('unite'),
      stock_actuel: toNumOrNull(formData.get('stock_actuel')) ?? 0,
      seuil_stock_min: toNumOrNull(formData.get('seuil_stock_min')),
      origine: formData.get('origine') || null,
      prix_par_type: {
        // Fallback sur le prix unitaire si le champ "Prix par type" est vide → toujours un nombre valide
        prix_gms: toNumOrNull(formData.get('prix_gms')) ?? prixUnitaire,
        prix_boutique: toNumOrNull(formData.get('prix_boutique')) ?? prixUnitaire,
        prix_particulier: toNumOrNull(formData.get('prix_particulier')) ?? prixUnitaire,
      }
    };

    try {
      const url = editingProduit ? `${API_URL}/api/produits/${editingProduit.id}` : `${API_URL}/api/produits`;
      const method = editingProduit ? 'PUT' : 'POST';

      const response = await apiCall(url, {
        method: method,
        body: JSON.stringify(produitData)
      });

      if (response.ok) {
        loadProduits();
        setShowProduitForm(false);
        setEditingProduit(null);
        showNotification(editingProduit ? 'Produit modifié avec succès' : 'Produit créé avec succès', 'success');
      } else {
        // 🔍 Surface le message d'erreur backend pour que l'utilisateur sache pourquoi ça a échoué
        let detail = `Erreur ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            // FastAPI 422 renvoie une liste d'objets {loc, msg, type, ...} → la formater proprement
            if (Array.isArray(errorData.detail)) {
              detail = errorData.detail
                .map((d) => `${(d.loc || []).slice(-1)[0] || 'champ'}: ${d.msg}`)
                .join(' • ');
            } else if (typeof errorData.detail === 'string') {
              detail = errorData.detail;
            }
          }
        } catch (_) { /* réponse non JSON */ }
        console.error('Erreur sauvegarde produit:', detail, produitData);
        showNotification(`Erreur lors de la sauvegarde : ${detail}`, 'error');
      }
    } catch (error) {
      // 🌐 Réseau / CORS / fetch a throw → afficher le maximum d'infos
      console.error('Erreur réseau sauvegarde produit:', error, produitData);
      const msg = error?.message || String(error) || 'Erreur réseau inconnue';
      showNotification(`Erreur réseau sauvegarde produit : ${msg}. Vérifie la console (F12) pour le détail.`, 'error');
    }
  };

  // Fonction pour sauvegarder un brouillon (crée une vraie commande avec statut brouillon)
  const saveBrouillon = async (e) => {
    e.preventDefault();
    // Récupérer le formulaire parent du bouton
    const form = e.target.closest('form');
    if (!form) {
      showNotification('Erreur: formulaire non trouvé', 'error');
      return;
    }
    const formData = new FormData(form);
    
    // Utiliser la même logique que handleCommandeSubmit mais avec statut brouillon
    const clientId = formData.get('client_id');
    const client = clients.find(c => c.id === clientId);
    
    if (!client) {
      showNotification('Veuillez sélectionner un client', 'error');
      return;
    }
    
    // Utiliser lignesProduits (React state) au lieu de FormData pour les produits
    const produitsCommande = [];
    for (const ligne of lignesProduits) {
      const produit = produits.find(p => p.id === ligne.produit_id);
      if (produit && ligne.quantite > 0) {
        let prixDefaut = produit.prix_unitaire;
        if (produit.prix_par_type && client && settings?.types_clients) {
          const typeIndex = settings.types_clients.findIndex(type => 
            type.toLowerCase() === client.type_client.toLowerCase()
          );
          
          switch (typeIndex) {
            case 0:
              prixDefaut = produit.prix_par_type.prix_gms || produit.prix_unitaire;
              break;
            case 1:
              prixDefaut = produit.prix_par_type.prix_boutique || produit.prix_unitaire;
              break;
            case 2:
              prixDefaut = produit.prix_par_type.prix_particulier || produit.prix_unitaire;
              break;
            default:
              prixDefaut = produit.prix_unitaire;
          }
        }
        
        const prixApplique = ligne.prix_personnalise && parseDecimal(ligne.prix_personnalise) > 0 
          ? parseDecimal(ligne.prix_personnalise) 
          : prixDefaut;
        
        // Déterminer l'origine : origine du produit ou origine par défaut
        const origine = produit.origine || settings?.origine_defaut || null;
        
        produitsCommande.push({
          produit_id: produit.id,
          nom_produit: produit.nom,
          quantite: parseDecimal(ligne.quantite),
          prix_unitaire: prixApplique,
          prix_defaut: prixDefaut,
          unite: produit.unite,
          bio: produit.bio || false,
          origine: origine
        });
      }
    }

    if (produitsCommande.length === 0) {
      showNotification('Veuillez ajouter au moins un produit', 'error');
      return;
    }

    const totalProduits = produitsCommande.reduce((sum, p) => sum + Math.round(p.quantite * p.prix_unitaire * 100), 0) / 100;
    const fraisLivraison = parseDecimal(formData.get('frais_livraison')) ?? 0;

    const commandeData = {
      client_id: clientId,
      nom_client: client.nom_entreprise,
      produits: produitsCommande,
      total: totalProduits,
      frais_livraison: fraisLivraison,
      statut: 'brouillon' // STATUT BROUILLON
    };

    // Ajouter le commentaire s'il existe
    const commentaire = formData.get('commentaire');
    if (commentaire && commentaire.trim()) {
      commandeData.commentaire = commentaire.trim();
    }

    const dateCommande = formData.get('date_commande');
    if (dateCommande) {
      const date = new Date(dateCommande);
      commandeData.date_commande = date.toISOString();
    }

    try {
      let response;
      
      if (editingCommande && editingCommande.statut === 'brouillon') {
        // Mise à jour d'un brouillon existant
        response = await apiCall(`${API_URL}/api/commandes/${editingCommande.id}`, {
          method: 'PUT',
          body: JSON.stringify(commandeData)
        });
      } else {
        // Création d'un nouveau brouillon
        response = await apiCall(`${API_URL}/api/commandes`, {
          method: 'POST',
          body: JSON.stringify(commandeData)
        });
      }

      if (response.ok) {
        const result = await response.json();
        // 🔒 Le backend peut renvoyer HTTP 200 avec { success: false } si une vérification échoue.
        // S'assurer qu'on a bien une commande créée avant d'annoncer le succès.
        if (result && result.success === false) {
          let msg = result.message || 'Le brouillon n\'a pas pu être sauvegardé';
          if (result.produits_stock_insuffisant && result.produits_stock_insuffisant.length > 0) {
            const liste = result.produits_stock_insuffisant
              .map((p) => `${p.nom} (stock: ${p.stock_actuel}, demandé: ${p.quantite_demandee})`)
              .join(', ');
            msg = `Stock insuffisant : ${liste}`;
          }
          showNotification(msg, 'error');
          return;
        }
        showNotification('Brouillon sauvegardé avec succès', 'success');
        setShowCommandeForm(false);
        setEditingCommande(null);
        await loadCommandes(); // Recharger pour voir le brouillon
      } else {
        let msg = 'Erreur lors de la sauvegarde du brouillon';
        try {
          const data = await response.json();
          msg = data.detail || msg;
        } catch (_) { /* noop */ }
        showNotification(msg, 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du brouillon:', error);
      showNotification('Erreur lors de la sauvegarde du brouillon', 'error');
    }
  };

  // Supprimer les anciennes fonctions de brouillon localStorage
  // const loadBrouillon, deleteBrouillon, etc. - on ne les utilise plus

  // Fonctions pour gérer les lignes de produits
  const ajouterLigneProduit = () => {
    setLignesProduits([...lignesProduits, { 
      id: Date.now(), 
      produit_id: '', 
      quantite: '', 
      prix_personnalise: '',
      update_prix: false
    }]);
  };

  const supprimerLigneProduit = (index) => {
    if (lignesProduits.length > 1) {
      const nouvellesLignes = lignesProduits.filter((_, i) => i !== index);
      setLignesProduits(nouvellesLignes);
    }
  };

  const updateLigneProduit = (index, field, value) => {
    const nouvellesLignes = [...lignesProduits];
    nouvellesLignes[index][field] = value;
    setLignesProduits(nouvellesLignes);
  };

  // Réinitialiser les lignes quand on ouvre/ferme le formulaire
  const openCommandeForm = (commande = null) => {
    if (commande) {
      // Mode édition - charger les produits existants
      setEditingCommande(commande);

      // Helper local : recalcule le prix par défaut courant du produit pour le client de cette commande
      // (sert à détecter visuellement les prix custom dans le formulaire).
      const getPrixDefautActuel = (produitId) => {
        const produit = produits.find((p) => p.id === produitId);
        if (!produit) return 0;
        const client = clients.find((c) => c.id === commande.client_id);
        if (produit.prix_par_type && client && settings?.types_clients) {
          const typeIndex = settings.types_clients.findIndex(
            (t) => t.toLowerCase() === (client.type_client || '').toLowerCase()
          );
          if (typeIndex === 0) return produit.prix_par_type.prix_gms || produit.prix_unitaire;
          if (typeIndex === 1) return produit.prix_par_type.prix_boutique || produit.prix_unitaire;
          if (typeIndex === 2) return produit.prix_par_type.prix_particulier || produit.prix_unitaire;
        }
        return produit.prix_unitaire;
      };

      const lignesExistantes = commande.produits.map(p => ({
        id: Date.now() + Math.random(),
        produit_id: p.produit_id,
        quantite: p.quantite,
        // 🔑 Toujours pré-remplir le champ prix avec le prix effectivement utilisé sur la commande
        prix_personnalise: p.prix_unitaire,
        // Conserver le prix par défaut pour pouvoir détecter visuellement le prix custom
        prix_defaut: getPrixDefautActuel(p.produit_id) || p.prix_defaut || p.prix_unitaire,
        update_prix: false
      }));
      setLignesProduits(lignesExistantes.length > 0 ? lignesExistantes : [
        { id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '', update_prix: false }
      ]);
    } else {
      // Nouvelle commande
      setEditingCommande(null);
      setLignesProduits([{ id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '', update_prix: false }]);
    }
    setShowCommandeForm(true);
  };

  const closeCommandeForm = () => {
    setShowCommandeForm(false);
    setEditingCommande(null);
    setLignesProduits([{ id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '', update_prix: false }]);
  };

  // Fonction pour filtrer les commandes
  const getFilteredCommandes = () => {
    let filtered = [...commandes];
    
    // Filtre par client
    if (commandeClientFilter) {
      filtered = filtered.filter(commande => commande.client_id === commandeClientFilter);
    }
    
    // Filtre par recherche de numéros
    if (commandeSearchTerm) {
      const searchLower = commandeSearchTerm.toLowerCase();
      filtered = filtered.filter(commande => {
        const commandeId = (commande.id || '').toLowerCase();
        const numeroFacture = (commande.numero_facture || '').toLowerCase();
        const numeroAvoir = (commande.numero_avoir || '').toLowerCase();
        
        return commandeId.includes(searchLower) || 
               numeroFacture.includes(searchLower) || 
               numeroAvoir.includes(searchLower);
      });
    }
    
    return filtered;
  };

  // Fonction pour mapper les anciens types vers les nouveaux
  const getDisplayClientType = (clientType) => {
    if (!settings?.types_clients) {
      return clientType; // Pas de settings chargés, afficher le type tel quel
    }
    
    // Mapping des anciens types vers les nouveaux
    const typeMapping = {
      'particulier': settings.types_clients[2] || 'particulier', // 3ème type
      'boutique': settings.types_clients[1] || 'boutique',       // 2ème type  
      'GMS': settings.types_clients[0] || 'GMS'                  // 1er type
    };
    
    return typeMapping[clientType] || clientType;
  };

  // Charger les brouillons depuis la base de données
  const getBrouillons = () => {
    return commandes.filter(commande => commande.statut === 'brouillon');
  };

  const handleCommandeSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const clientId = formData.get('client_id');
    const client = clients.find(c => c.id === clientId);
    
    // Collecter les produits depuis lignesProduits (React state)
    const produitsCommande = [];
    
    for (let ligne of lignesProduits) {
      if (ligne.produit_id && ligne.quantite && parseDecimal(ligne.quantite) > 0) {
        const produit = produits.find(p => p.id === ligne.produit_id);
        if (produit) {
          // Calculer le prix selon le type de client
          let prixDefaut = produit.prix_unitaire;
          let tierToSave = 'default';  // 🆕 Quel champ mettre à jour si "Mémoriser ce prix" est coché
          if (produit.prix_par_type && client && settings?.types_clients) {
            const typeIndex = settings.types_clients.findIndex(type => 
              type.toLowerCase() === client.type_client.toLowerCase()
            );
            
            switch (typeIndex) {
              case 0: // Premier type (ex: GMS)
                prixDefaut = produit.prix_par_type.prix_gms || produit.prix_unitaire;
                tierToSave = 'prix_gms';
                break;
              case 1: // Deuxième type (ex: boutique)
                prixDefaut = produit.prix_par_type.prix_boutique || produit.prix_unitaire;
                tierToSave = 'prix_boutique';
                break;
              case 2: // Troisième type (ex: particulier)
                prixDefaut = produit.prix_par_type.prix_particulier || produit.prix_unitaire;
                tierToSave = 'prix_particulier';
                break;
              default:
                // Si le type n'est pas trouvé, utiliser le prix unitaire
                prixDefaut = produit.prix_unitaire;
            }
          }
          
          const prixApplique = ligne.prix_personnalise && parseDecimal(ligne.prix_personnalise) > 0 
            ? parseDecimal(ligne.prix_personnalise) 
            : prixDefaut;
          
          // Déterminer l'origine : origine du produit ou origine par défaut
          const origine = produit.origine || settings?.origine_defaut || null;
          
          produitsCommande.push({
            produit_id: produit.id,
            nom_produit: produit.nom,
            quantite: parseDecimal(ligne.quantite),
            prix_unitaire: prixApplique,
            prix_defaut: prixDefaut,
            unite: produit.unite,
            bio: produit.bio || false,
            variete: produit.variete || null,  // 🆕 Variété du produit
            origine: origine,
            update_prix: ligne.update_prix || false,  // 🆕 Flag pour sauvegarder le nouveau prix
            tier_to_save: tierToSave  // 🆕 'prix_gms' | 'prix_boutique' | 'prix_particulier' | 'default'
          });
        }
      }
    }

    // Calculer le total des produits
    const totalProduits = produitsCommande.reduce((sum, p) => sum + Math.round(p.quantite * p.prix_unitaire * 100), 0) / 100;
    
    // Récupérer les frais de livraison
    const fraisLivraison = parseDecimal(formData.get('frais_livraison')) ?? 0;
    
    // Total général = produits + frais de livraison
    const total = totalProduits + fraisLivraison;

    const commandeData = {
      client_id: clientId,
      nom_client: client.nom_entreprise,
      produits: produitsCommande,
      total: totalProduits, // Le total reste le total des produits seuls pour compatibilité
      frais_livraison: fraisLivraison,
      statut: (editingCommande?.statut === 'brouillon') ? 'en_cours' : (editingCommande ? editingCommande.statut : 'en_cours')
    };

    // Ajouter le commentaire s'il existe
    const commentaire = formData.get('commentaire');
    if (commentaire && commentaire.trim()) {
      commandeData.commentaire = commentaire.trim();
    }

    // Ajouter la date de commande si elle est spécifiée
    const dateCommande = formData.get('date_commande');
    if (dateCommande) {
      // Créer une date en UTC pour éviter les problèmes de fuseau horaire
      // dateCommande est au format YYYY-MM-DD (input date HTML)
      const [year, month, day] = dateCommande.split('-');
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
      commandeData.date_commande = date.toISOString();
    } else if (!editingCommande) {
      // Pour une nouvelle commande sans date, la date par défaut sera gérée par le backend
      // Pour une commande existante, on ne modifie pas la date si elle n'est pas fournie
    }

    try {
      const url = editingCommande ? `${API_URL}/api/commandes/${editingCommande.id}` : `${API_URL}/api/commandes`;
      const method = editingCommande ? 'PUT' : 'POST';
      
      const response = await apiCall(url, {
        method: method,
        body: JSON.stringify(commandeData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success !== false) {
          showNotification(editingCommande ? 'Commande mise à jour avec succès' : 'Commande créée avec succès', 'success');
          setShowCommandeForm(false);
          setEditingCommande(null);
          await refreshDashboard(); // Attendre le rafraîchissement complet
        } else {
          showNotification(result.message || 'Problème avec le stock', 'warning');
        }
      } else {
        // 🛟 Remonter le message d'erreur précis du backend (ex: "Impossible de modifier une commande facturée qui n'est pas la plus récente...")
        let detail = '';
        try {
          const err = await response.json();
          detail = err?.detail || err?.message || '';
        } catch (_) {
          detail = await response.text().catch(() => '') || '';
        }
        showNotification(
          detail ? `Sauvegarde refusée : ${detail}` : `Erreur ${response.status} lors de la sauvegarde de la commande`,
          'error'
        );
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la commande:', error);
      showNotification(`Erreur réseau : ${error?.message || error}`, 'error');
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const userData = {
      username: formData.get('username'),
      email: formData.get('email'),
      password: formData.get('password'),
      role: formData.get('role')
    };

    try {
      const response = await apiCall(`${API_URL}/api/auth/register`, {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        loadUsers();
        setShowUserForm(false);
        showNotification('Utilisateur créé avec succès', 'success');
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la création de l\'utilisateur', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      showNotification('Erreur lors de la création de l\'utilisateur', 'error');
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const settingsData = {
      nom_entreprise: formData.get('nom_entreprise'),
      adresse: formData.get('adresse'),
      siret: formData.get('siret'),
      numero_tva_intra: formData.get('numero_tva_intra'),
      numero_ecocert: formData.get('numero_ecocert'),
      en_conversion_bio: formData.get('en_conversion_bio') === 'on', // checkbox value
      numero_iban: formData.get('numero_iban'),
      capital_social: formData.get('capital_social'),
      couleur_primaire: formData.get('couleur_primaire'),
      logo_url: settings.logo_url || null, // CORRECTION: Préserver le logo existant
      seuil_stock_defaut: parseFloat(formData.get('seuil_stock_defaut')) || 10.0,
      gestion_stock_active: formData.get('gestion_stock_active') === 'on', // checkbox value
      devise: formData.get('devise') || 'EUR',
      origine_defaut: formData.get('origine_defaut') || null,
      // 📧 Personnalisation des emails sortants
      email_sender_name: formData.get('email_sender_name') || null,
      email_signature: formData.get('email_signature') || null,
      email_subject_template: formData.get('email_subject_template') || null,
      email_body_template: formData.get('email_body_template') || null,
      types_clients: [
        formData.get('type_client_1'),
        formData.get('type_client_2'),
        formData.get('type_client_3')
      ].filter(Boolean)
    };

    try {
      const response = await apiCall(`${API_URL}/api/settings`, {
        method: 'PUT',
        body: JSON.stringify(settingsData)
      });

      if (response.ok) {
        await loadSettings(); // Recharger les settings
        await loadClients(); // Recharger les clients pour appliquer les nouveaux types
        await loadCommandes(); // Recharger aussi les commandes au cas où
        // Mettre à jour immédiatement l'état de gestion du stock
        setGestionStockActive(settingsData.gestion_stock_active);
        showNotification('Paramètres mis à jour avec succès', 'success');
      } else {
        showNotification('Erreur lors de la mise à jour des paramètres', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
      showNotification('Erreur lors de la sauvegarde des paramètres', 'error');
    }
  };

  // Fonction pour charger les alertes de stock
  const loadStockAlerts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stock/alerts`, {
        credentials: 'include'  // 🔒 SÉCURITÉ : Utiliser le cookie HttpOnly
      });
      if (response.ok) {
        const data = await response.json();
        setStockAlerts(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des alertes de stock:', error);
    }
  };

  // Fonction pour charger l'historique des mouvements
  const loadStockMovements = async (productId = null) => {
    try {
      const endpoint = productId ? `${API_URL}/api/stock/mouvements/${productId}` : `${API_URL}/api/stock/mouvements`;
      const response = await fetch(endpoint, {
        credentials: 'include'  // 🔒 SÉCURITÉ : Utiliser le cookie HttpOnly
      });
      if (response.ok) {
        const data = await response.json();
        setStockMovements(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des mouvements de stock:', error);
    }
  };

  // Fonction pour ajouter du stock
  const handleStockSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const stockUpdates = [];
    
    // Récupérer tous les produits sélectionnés
    const productSelects = e.target.querySelectorAll('select[name="produit_id[]"]');
    const quantityInputs = e.target.querySelectorAll('input[name="quantite[]"]');
    
    for (let i = 0; i < productSelects.length; i++) {
      const productId = productSelects[i].value;
      const quantity = parseFloat(quantityInputs[i].value) || 0;
      
      if (productId && quantity > 0) {
        stockUpdates.push({
          produit_id: productId,
          quantite: quantity
        });
      }
    }
    
    if (stockUpdates.length === 0) {
      showNotification('Veuillez sélectionner au moins un produit et une quantité valide', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/produits/stock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',  // 🔒 SÉCURITÉ : Utiliser le cookie HttpOnly
        body: JSON.stringify(stockUpdates)
      });
      
      if (response.ok) {
        const data = await response.json();
        showNotification(`Stock mis à jour pour ${data.produits_mis_a_jour.length} produit(s)`, 'success');
        setShowStockForm(false);
        loadProduits();
        loadStockAlerts();
      } else {
        showNotification('Erreur lors de la mise à jour du stock', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la mise à jour du stock', 'error');
    }
  };

  // Fonctions pour la gestion avancée des utilisateurs
  const resetUserPassword = async (userId, username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser le mot de passe de ${username} ?`)) {
      return;
    }
    
    try {
      const response = await apiCall(`${API_URL}/api/auth/users/${userId}/reset-password`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        // Afficher la modale avec le nouveau mot de passe
        setResetPasswordData({
          username: username,
          newPassword: result.new_password
        });
        setShowPasswordResetModal(true);
        showNotification('Mot de passe réinitialisé avec succès', 'success');
      } else {
        showNotification('Erreur lors de la réinitialisation', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la réinitialisation', 'error');
    }
  };

  const copyPasswordToClipboard = () => {
    if (resetPasswordData?.newPassword) {
      navigator.clipboard.writeText(resetPasswordData.newPassword).then(() => {
        showNotification('Mot de passe copié dans le presse-papiers', 'success');
      }).catch(() => {
        showNotification('Erreur lors de la copie', 'error');
      });
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        showNotification('Rôle modifié avec succès', 'success');
        loadUsers(); // Recharger la liste
      } else {
        showNotification('Erreur lors de la modification du rôle', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la modification du rôle', 'error');
    }
  };

  const toggleUserActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'désactiver' : 'activer';
    
    try {
      const response = await apiCall(`${API_URL}/api/auth/users/${userId}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ active: !currentStatus })
      });

      if (response.ok) {
        showNotification(`Utilisateur ${action} avec succès`, 'success');
        loadUsers(); // Recharger la liste
      } else {
        showNotification(`Erreur lors de la ${action}ation`, 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification(`Erreur lors de la ${action}ation`, 'error');
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
          method: 'DELETE',
          credentials: 'include'  // 🔒 SÉCURITÉ : Utiliser le cookie HttpOnly
        });
        
        if (response.ok) {
          showNotification('Utilisateur supprimé avec succès', 'success');
          loadUsers();
        } else {
          const errorData = await response.json();
          showNotification(errorData.detail || 'Erreur lors de la suppression', 'error');
        }
      } catch (error) {
        console.error('Erreur:', error);
        showNotification('Erreur lors de la suppression de l\'utilisateur', 'error');
      }
    }
  };

  // Fonction pour gérer l'upload de logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/upload-logo`, {
        method: 'POST',
        credentials: 'include',  // 🔒 SÉCURITÉ : Utiliser le cookie HttpOnly
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        showNotification('Logo uploadé avec succès', 'success');
        loadSettings();
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de l\'upload du logo', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de l\'upload du logo', 'error');
    }
  };

  // Fonction pour calculer le CA d'un client
  const getClientCA = (clientId) => {
    return commandes
      .filter(commande => commande.client_id === clientId)
      .reduce((total, commande) => total + (commande.total || 0), 0);
  };

  // Fonctions de recherche
  const getFilteredClients = () => {
    let filtered = clients;

    // Filtre par type de client
    if (clientTypeFilter) {
      filtered = filtered.filter(client => client.type_client === clientTypeFilter);
    }

    // Filtre par CA minimum (numérique)
    if (clientCaFilter) {
      const caValue = parseFloat(clientCaFilter);
      if (!isNaN(caValue) && caValue > 0) {
        filtered = filtered.filter(client => getClientCA(client.id) >= caValue);
      }
    }

    // Recherche par mot-clé (nom, SIRET, email, téléphone)
    if (searchTermClients) {
      const searchTerm = searchTermClients.toLowerCase();
      filtered = filtered.filter(client =>
        (client.nom_entreprise || '').toLowerCase().includes(searchTerm) ||
        (client.siret || '').toLowerCase().includes(searchTerm) ||
        (client.email || '').toLowerCase().includes(searchTerm) ||
        (client.telephone || '').toLowerCase().includes(searchTerm) ||
        (client.adresse || '').toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  };

  const getFilteredProduits = () => {
    let filtered = produits;

    // Filtre par famille
    if (produitFamilleFilter) {
      filtered = filtered.filter(produit => produit.famille === produitFamilleFilter);
    }

    // Recherche par mot-clé (nom, description, famille)
    if (searchTermProduits) {
      const searchTerm = searchTermProduits.toLowerCase();
      filtered = filtered.filter(produit =>
        (produit.nom || '').toLowerCase().includes(searchTerm) ||
        (produit.description || '').toLowerCase().includes(searchTerm) ||
        (produit.famille || '').toLowerCase().includes(searchTerm)
      );
    }

    // Trier pour mettre les produits fin de saison en dernier
    filtered = filtered.sort((a, b) => {
      // Si un des produits est fin de saison et pas l'autre, mettre le fin de saison à la fin
      if (a.fin_de_saison && !b.fin_de_saison) return 1;
      if (!a.fin_de_saison && b.fin_de_saison) return -1;
      // Sinon, trier par nom alphabétiquement
      return (a.nom || '').localeCompare(b.nom || '');
    });

    return filtered;
  };

  // 🆕 Une commande peut être supprimée uniquement si c'est la plus récente affichée,
  // peu importe son statut (le backend applique la même règle, cohérence UI ↔ API).
  const canDeleteCommande = (commande) => {
    if (!commandes || commandes.length === 0) return false;
    // Trier par date_commande décroissante (même règle que la liste UI) — plus récent en premier
    const commandesTriees = [...commandes].sort((a, b) => {
      const getDate = (cmd) => {
        if (!cmd.date_commande) return 0;
        try { return new Date(cmd.date_commande).getTime() || 0; } catch { return 0; }
      };
      return getDate(b) - getDate(a);
    });
    return commande.id === commandesTriees[0].id;
  };
  const getPaginatedItems = (items, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items, itemsPerPage) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  // Composant de pagination réutilisable
  const Pagination = ({ items, currentPage, setCurrentPage, itemsPerPage, setItemsPerPage }) => {
    const totalPages = getTotalPages(items, itemsPerPage);

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <span>
            Affichage {Math.min((currentPage - 1) * itemsPerPage + 1, items.length)} à{' '}
            {Math.min(currentPage * itemsPerPage, items.length)} sur {items.length} éléments
          </span>
        </div>
        
        <div className="pagination-controls">
          <div className="items-per-page">
            <label>Éléments par page:</label>
            <select 
              value={itemsPerPage} 
              onChange={(e) => {
                const newItemsPerPage = parseInt(e.target.value);
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1); // Reset à la première page
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="page-navigation">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary btn-sm"
            >
              ← Précédent
            </button>
            
            <span className="page-info">
              Page {currentPage} sur {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary btn-sm"
            >
              Suivant →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Note: Le filtrage des clients se fait maintenant dans getFilteredClients()

  // Fonction pour filtrer les produits
  const filterProduits = () => {
    let filtered = [...produits];
    
    // Filtrer par famille
    if (produitFamilleFilter) {
      filtered = filtered.filter(produit => 
        produit.famille && produit.famille.toLowerCase() === produitFamilleFilter.toLowerCase()
      );
    }
    
    setFilteredProduits(filtered);
  };

  // Effet pour filtrer les produits quand les filtres changent
  useEffect(() => {
    if (produits.length > 0) {
      filterProduits();
    }
  }, [produits, produitFamilleFilter]);

  // Note: Le filtrage des clients se fait maintenant directement dans getFilteredClients()

  // Fonction pour mettre à jour le prix par défaut selon le type de client
  const updatePrixDefaut = (e) => {
    const select = e.target;
    const option = select.selectedOptions[0];
    if (!option) return;

    const prixDefaut = parseFloat(option.dataset.prixDefaut) || 0;
    const prixGms = parseFloat(option.dataset.prixGms) || prixDefaut;
    const prixBoutique = parseFloat(option.dataset.prixBoutique) || prixDefaut;
    const prixParticulier = parseFloat(option.dataset.prixParticulier) || prixDefaut;

    // Récupérer le type de client sélectionné
    const clientSelect = document.getElementById('client-select');
    const clientId = clientSelect?.value;
    const client = clients.find(c => c.id === clientId);
    
    let prixApplique = prixDefaut;
    if (client) {
      switch (client.type_client.toLowerCase()) {
        case 'gms':
          prixApplique = prixGms;
          break;
        case 'boutique':
          prixApplique = prixBoutique;
          break;
        case 'particulier':
          prixApplique = prixParticulier;
          break;
        default:
          prixApplique = prixDefaut;
      }
    }

    // Mettre à jour l'affichage du prix par défaut
    const ligne = select.closest('.produit-ligne');
    const prixInfo = ligne?.querySelector('.prix-defaut');
    if (prixInfo) {
      prixInfo.textContent = `Prix par défaut: ${formatCurrency(prixApplique, settings?.devise)}`;
    }
  };

  const updateCommandeStatut = async (commandeId, nouveauStatut) => {
    // Trouver la commande
    const commande = commandes.find(c => c.id === commandeId);
    if (!commande) {
      showNotification('Commande non trouvée', 'error');
      return;
    }

    // Vérifier les restrictions de changement de statut
    if (commande.statut === 'facture') {
      // Une commande facturée peut passer en statut annulé (pour les remboursements)
      if (nouveauStatut !== 'facture' && nouveauStatut !== 'annule') {
        showNotification('Une commande facturée ne peut passer qu\'en statut annulé', 'error');
        return;
      }
    }

    try {
      const response = await apiCall(`${API_URL}/api/commandes/${commandeId}/statut`, {
        method: 'PUT',
        body: JSON.stringify({ statut: nouveauStatut })
      });

      if (response.ok) {
        await refreshDashboard(); // Attendre le rafraîchissement complet
        showNotification('Statut mis à jour avec succès', 'success');
      } else {
        // 🛟 Remonter le message d'erreur précis du backend
        let detail = '';
        try {
          const err = await response.json();
          detail = err?.detail || err?.message || '';
        } catch (_) {
          detail = await response.text().catch(() => '') || '';
        }
        showNotification(
          detail ? `Changement de statut refusé : ${detail}` : `Erreur ${response.status} lors de la mise à jour du statut`,
          'error'
        );
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      showNotification(`Erreur réseau : ${error?.message || error}`, 'error');
    }
  };

  // Fonction pour gérer l'export comptable
  const handleExportComptable = async () => {
    const exportType = document.querySelector('input[name="export_type"]:checked')?.value;

    let url;
    const params = new URLSearchParams();
    let fileName = 'export.xlsx';

    if (exportType === 'period') {
      const dateDebut = document.querySelector('input[name="date_debut"]')?.value;
      const dateFin = document.querySelector('input[name="date_fin"]')?.value;

      if (!dateDebut || !dateFin) {
        showNotification('Veuillez sélectionner une période', 'error');
        return;
      }
      params.append('date_debut', dateDebut);
      params.append('date_fin', dateFin);
      url = `${API_URL}/api/export-comptable`;
      fileName = `export_comptable_${dateDebut}_${dateFin}.xlsx`;
    } else if (exportType === 'annual') {
      const annee = document.querySelector('select[name="annee"]')?.value;
      if (!annee) {
        showNotification('Veuillez sélectionner une année', 'error');
        return;
      }
      params.append('annee', annee);
      url = `${API_URL}/api/export-comptable`;
      fileName = `export_comptable_${annee}.xlsx`;
    } else if (exportType === 'stock_vendu') {
      const dateDebut = document.querySelector('input[name="stock_date_debut"]')?.value;
      const dateFin = document.querySelector('input[name="stock_date_fin"]')?.value;

      if (!dateDebut || !dateFin) {
        showNotification('Veuillez sélectionner une période', 'error');
        return;
      }
      params.append('date_debut', dateDebut);
      params.append('date_fin', dateFin);
      url = `${API_URL}/api/export-stock-vendu`;
      fileName = `stock_vendu_${dateDebut}_${dateFin}.xlsx`;
    } else {
      showNotification("Veuillez sélectionner un type d'export", 'error');
      return;
    }

    try {
      const response = await apiCall(`${url}?${params.toString()}`, {
        method: 'GET'
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        showNotification('Export téléchargé avec succès', 'success');
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || "Erreur lors de l'export", 'error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  };

  // Fonction pour charger l'historique d'un client
  const loadClientHistory = async (clientId) => {
    setLoadingClientHistory(true);
    try {
      // Récupérer toutes les commandes du client
      const response = await apiCall(`${API_URL}/api/commandes`);
      if (response.ok) {
        const allOrders = await response.json();
        const clientOrders = allOrders.filter(order => order.client_id === clientId);
        
        // Trier les commandes par date (plus récentes en premier)
        const sortedOrders = clientOrders.sort((a, b) => {
          const dateA = a.date_commande ? new Date(a.date_commande) : new Date(0);
          const dateB = b.date_commande ? new Date(b.date_commande) : new Date(0);
          return dateB - dateA;
        });
        
        setClientOrderHistory(sortedOrders);
        
        // Calculer les statistiques par produit
        const productStats = {};
        clientOrders.forEach(order => {
          if (order.produits && Array.isArray(order.produits)) {
            order.produits.forEach(produit => {
              const key = produit.nom_produit || 'Produit inconnu';
              if (!productStats[key]) {
                productStats[key] = {
                  nom_produit: key,
                  quantite_totale: 0,
                  nombre_commandes: 0,
                  chiffre_affaires: 0,
                  unite: produit.unite || 'unité'
                };
              }
              productStats[key].quantite_totale += parseFloat(produit.quantite || 0);
              productStats[key].chiffre_affaires += parseFloat(produit.quantite || 0) * parseFloat(produit.prix_unitaire || 0);
              productStats[key].nombre_commandes += 1;
            });
          }
        });
        
        // Convertir en array et trier par chiffre d'affaires
        const statsArray = Object.values(productStats).sort((a, b) => b.chiffre_affaires - a.chiffre_affaires);
        setClientProductStats(statsArray);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique client:', error);
      showNotification('Erreur lors du chargement de l\'historique', 'error');
    } finally {
      setLoadingClientHistory(false);
    }
  };

  // Fonction pour ouvrir le détail d'un client
  const openClientDetail = async (client) => {
    setSelectedClientDetail(client);
    setShowClientDetail(true);
    // Bloquer le scroll de la page principale
    document.body.style.overflow = 'hidden';
    await loadClientHistory(client.id);
  };

  // Fonction pour fermer le détail client
  const closeClientDetail = () => {
    setShowClientDetail(false);
    setSelectedClientDetail(null);
    setClientOrderHistory([]);
    setClientProductStats([]);
    // Réactiver le scroll de la page principale
    document.body.style.overflow = 'unset';
  };

  // Fonction pour changer le mot de passe
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const currentPassword = formData.get('current_password');
    const newPassword = formData.get('new_password');
    const confirmPassword = formData.get('confirm_password');
    
    if (newPassword !== confirmPassword) {
      showNotification('Les nouveaux mots de passe ne correspondent pas', 'error');
      return;
    }
    
    if (newPassword.length < 8) {
      showNotification('Le nouveau mot de passe doit contenir au moins 8 caractères', 'error');
      return;
    }
    
    try {
      const response = await apiCall(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (response.ok) {
        showNotification('Mot de passe changé avec succès', 'success');
        setShowPasswordChangeModal(false);
        setNeedsPasswordChange(false);
        
        // 🎉 Afficher l'onboarding si ce n'est pas déjà fait
        if (!onboardingCompleted) {
          setTimeout(() => {
            setShowOnboarding(true);
          }, 500); // Petit délai pour une transition smooth
        }
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors du changement de mot de passe', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors du changement de mot de passe', 'error');
    }
  };

  // Fonctions de gestion de l'onboarding
  const handleOnboardingComplete = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/complete-onboarding`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setShowOnboarding(false);
        setOnboardingCompleted(true);
        showNotification('🎉 Bienvenue dans Maraich\'Pro ! Vous êtes prêt à démarrer.', 'success');
        // Rafraîchir les données utilisateur
        await checkAuth();
      }
    } catch (error) {
      console.error('Erreur lors de la complétion de l\'onboarding:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/skip-onboarding`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setShowOnboarding(false);
        setOnboardingCompleted(true);
        showNotification('Vous pouvez relancer la visite guidée à tout moment !', 'info');
        // Rafraîchir les données utilisateur
        await checkAuth();
      }
    } catch (error) {
      console.error('Erreur lors du skip de l\'onboarding:', error);
    }
  };

  const handleRestartOnboarding = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/restart-onboarding`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setOnboardingCompleted(false);
        setShowOnboarding(true);
        showNotification('Visite guidée relancée !', 'success');
        // Rafraîchir les données utilisateur
        await checkAuth();
      }
    } catch (error) {
      console.error('Erreur lors du restart de l\'onboarding:', error);
    }
  };

  // Fonction pour basculer l'expansion d'une ligne sur mobile
  const toggleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const deleteClient = async (clientId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        const response = await apiCall(`${API_URL}/api/clients/${clientId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadClients();
          showNotification('Client supprimé avec succès', 'success');
        } else {
          showNotification('Erreur lors de la suppression du client', 'error');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression du client:', error);
        showNotification('Erreur lors de la suppression du client', 'error');
      }
    }
  };

  const deleteProduit = async (produitId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        const response = await apiCall(`${API_URL}/api/produits/${produitId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadProduits();
          showNotification('Produit supprimé avec succès', 'success');
        } else {
          showNotification('Erreur lors de la suppression du produit', 'error');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression du produit:', error);
        showNotification('Erreur lors de la suppression du produit', 'error');
      }
    }
  };

  const toggleFinDeSaison = async (produit) => {
    try {
      const response = await apiCall(`${API_URL}/api/produits/${produit.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...produit,
          fin_de_saison: !produit.fin_de_saison
        })
      });

      if (response.ok) {
        loadProduits();
        showNotification(
          produit.fin_de_saison ? 'Produit réactivé avec succès' : 'Produit marqué fin de saison',
          'success'
        );
      } else {
        showNotification('Erreur lors de la mise à jour du produit', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du produit:', error);
      showNotification('Erreur lors de la mise à jour du produit', 'error');
    }
  };

  const deleteCommande = async (commandeId) => {
    // Trouver la commande à supprimer (pour l'affichage dans la confirmation)
    const commande = commandes.find(c => c.id === commandeId);
    if (!commande) {
      showNotification('Commande non trouvée', 'error');
      return;
    }

    // 🆕 On délègue la règle métier au backend (seul juge de ce qui peut être supprimé).
    // Le backend bloque uniquement les commandes facturées / annulées (protection légale)
    // et laisse supprimer librement les brouillons + commandes non facturées.
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      return;
    }

    try {
      const response = await apiCall(`${API_URL}/api/commandes/${commandeId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        loadCommandes();
        showNotification('Commande supprimée avec succès', 'success');
        return;
      }
      // 🛟 Remonter le message précis renvoyé par le backend (au lieu d'un générique)
      let detail = '';
      try {
        const err = await response.json();
        detail = err?.detail || err?.message || '';
      } catch (_) {
        detail = await response.text().catch(() => '') || '';
      }
      const msg = detail
        ? `Suppression refusée : ${detail}`
        : `Erreur ${response.status} lors de la suppression de la commande`;
      showNotification(msg, 'error');
    } catch (error) {
      console.error('Erreur réseau suppression commande:', error);
      showNotification(`Erreur réseau : ${error?.message || error}. Vérifie la console (F12).`, 'error');
    }
  };

  // États pour les modals PDF mobile
  const [showMobilePDF, setShowMobilePDF] = useState(false);
  const [mobilePDFContent, setMobilePDFContent] = useState('');
  const [mobilePDFTitle, setMobilePDFTitle] = useState('');
  // true = mobilePDFContent est une blob URL (PDF) ; false = HTML brut (étiquettes, BL, avoir)
  const [mobilePDFIsBlobUrl, setMobilePDFIsBlobUrl] = useState(false);

  // États pour la modale de mot de passe reset
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState(null);

  // 📲 Référence vers l'iframe du modal PDF mobile (pour pouvoir l'imprimer)
  const mobilePDFIframeRef = useRef(null);

  // NOUVEAU: Fonction d'impression pour mobile PDF
  const printMobilePDF = () => {
    const iframe = mobilePDFIframeRef.current;

    // 🥇 Méthode préférée — fonctionne sur iPad/Safari, Chrome Android, Firefox mobile :
    // on cible directement la fenêtre de l'iframe (même origine grâce à srcDoc / blob URL).
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        return;
      } catch (_) {
        // Fallback ci-dessous si jamais print() est bloqué par le navigateur
      }
    }

    // Fallback historique (desktop avec popups autorisés)
    if (mobilePDFIsBlobUrl) {
      const w = window.open(mobilePDFContent, '_blank');
      if (w) {
        try { w.focus(); } catch (_) { /* noop */ }
      }
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showNotification('Le navigateur bloque l\'ouverture des popups — autorisez-les pour imprimer', 'warning');
      return;
    }
    printWindow.document.write(mobilePDFContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // NOUVEAU: Détection mobile/tablet améliorée
  const isMobileDevice = () => {
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isSmallScreen = window.innerWidth <= 1024;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Détection améliorée : vérifier aussi la viewport actuelle via document
    const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const screenWidth = window.screen?.width || 0;
    const isSmallViewport = viewportWidth <= 1024 || screenWidth <= 1024;
    
    // Forcer le mode mobile pour les tests si certaines conditions sont réunies
    const isTestEnvironment = navigator.userAgent.includes('HeadlessChrome');
    const forceTabletMode = isTestEnvironment && (viewportWidth <= 1024 || window.screen?.width <= 1024);
    
    const result = isMobileUA || isSmallScreen || isTouchDevice || isSmallViewport || forceTabletMode;
    
    console.log('🔍 Mobile Detection Enhanced:', { 
      userAgent: navigator.userAgent, 
      windowWidth: window.innerWidth,
      viewportWidth,
      screenWidth,
      isMobileUA, 
      isSmallScreen, 
      isTouchDevice, 
      isSmallViewport,
      isTestEnvironment,
      forceTabletMode,
      result 
    });
    
    return result;
  };

  const genererEtiquettes = async (commandeId) => {
    // 📲 Tablette/mobile : ouvrir directement les étiquettes dans un nouvel onglet plein écran
    if (isMobileDevice()) {
      window.open(`${API_URL}/api/etiquettes-cagette/${commandeId}`, '_blank');
      return;
    }
    try {
      const response = await apiCall(`${API_URL}/api/etiquettes-cagette/${commandeId}`, {
        method: 'GET'
      });
      if (response.ok) {
        const html = await response.text();
        const newWindow = window.open('', '_blank');
        newWindow.document.write(html);
        newWindow.document.close();
        showNotification('Étiquettes prêtes à imprimer', 'success');
      } else {
        showNotification('Erreur lors de la génération des étiquettes', 'error');
      }
    } catch (error) {
      console.error('Erreur génération étiquettes:', error);
      showNotification('Erreur lors de la génération des étiquettes', 'error');
    }
  };

  const genererBonLivraison = async (commandeId) => {
    // 📲 Tablette/mobile : ouvrir directement le BL dans un nouvel onglet plein écran
    if (isMobileDevice()) {
      window.open(`${API_URL}/api/bon-livraison/${commandeId}`, '_blank');
      return;
    }

    try {
      const response = await apiCall(`${API_URL}/api/bon-livraison/${commandeId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const html = await response.text();
        const newWindow = window.open('', '_blank');
        newWindow.document.write(html);
        newWindow.document.close();
        showNotification('Bon de livraison généré avec succès', 'success');
      } else {
        showNotification('Erreur lors de la génération du bon de livraison', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la génération du bon de livraison:', error);
      showNotification('Erreur lors de la génération du bon de livraison', 'error');
    }
  };

  const genererFacture = async (commandeId) => {
    // 📲 Sur tablette/mobile, ouvrir directement la facture dans un nouvel onglet
    // (le navigateur a son propre visualiseur + bouton "Imprimer" qui fonctionnent bien
    // mieux qu'un iframe dans un modal — surtout sur iPad/Safari).
    if (isMobileDevice()) {
      window.open(`${API_URL}/api/facture/${commandeId}`, '_blank');
      refreshDashboard();
      return;
    }

    try {
      const response = await apiCall(`${API_URL}/api/facture/${commandeId}`, {
        method: 'GET'
      });

      if (response.ok) {
        // Desktop : ouvrir dans un nouvel onglet avec le HTML déjà téléchargé
        const html = await response.text();
        const newWindow = window.open('', '_blank');
        newWindow.document.write(html);
        newWindow.document.close();

        showNotification('Facture générée avec succès', 'success');
        refreshDashboard();
      } else {
        let errorMsg = 'Erreur lors de la génération de la facture';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (_) { /* réponse non JSON */ }
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la génération de la facture:', error);
      showNotification('Erreur lors de la génération de la facture', 'error');
    }
  };

  const genererAvoir = async (commandeId) => {
    // 📲 Tablette/mobile : ouvrir directement l'avoir dans un nouvel onglet plein écran
    if (isMobileDevice()) {
      window.open(`${API_URL}/api/avoir/${commandeId}`, '_blank');
      await loadCommandes();
      return;
    }

    try {
      const response = await apiCall(`${API_URL}/api/avoir/${commandeId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const html = await response.text();
        const newWindow = window.open('', '_blank');
        newWindow.document.write(html);
        newWindow.document.close();

        showNotification('Avoir généré avec succès', 'success');
        await loadCommandes();
      } else {
        let errorMsg = 'Erreur lors de la génération de l\'avoir';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (_) { /* réponse non JSON */ }
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la génération de l\'avoir:', error);
      showNotification('Erreur lors de la génération de l\'avoir', 'error');
    }
  };

  const getPrixPourClient = (produit, typeClient) => {
    if (!produit.prix_par_type) return produit.prix_unitaire;
    
    switch (typeClient) {
      case 'GMS':
        return produit.prix_par_type.prix_gms || produit.prix_unitaire;
      case 'boutique':
        return produit.prix_par_type.prix_boutique || produit.prix_unitaire;
      case 'particulier':
        return produit.prix_par_type.prix_particulier || produit.prix_unitaire;
      default:
        return produit.prix_unitaire;
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('activeTab', tab); // Sauvegarder l'onglet actif
    setIsMobileMenuOpen(false);
    if (tab === 'statistiques') {
      loadStatistiques();
      loadStatistiquesProduits();
      loadStatistiquesOrigines();
    }
    if (tab === 'reglages') {
      loadSettings();
    }
  };

  // Exposer handleTabChange globalement pour l'onboarding
  useEffect(() => {
    window.handleTabChange = handleTabChange;
    return () => {
      delete window.handleTabChange;
    };
  }, []);

  // Écran de connexion
  if (showLoginForm) {
    return (
      <LoginForm
        settings={settings}
        loginError={loginError}
        onLogin={handleLogin}
        onForgotPassword={handleForgotPassword}
        onResetPassword={handleResetPassword}
      />
    );
  }

  // Composant de notification toast
  const NotificationToast = ({ notification, onRemove }) => (
    <div className={`notification-toast ${notification.type}`}>
      <div className="notification-content">
        <div className="notification-message" style={{ whiteSpace: 'pre-wrap' }}>{notification.message}</div>
        <button 
          className="notification-close"
          onClick={() => onRemove(notification.id)}
        >
          ×
        </button>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <Dashboard
      nbCommandesAffichees={nbCommandesAffichees}
      setNbCommandesAffichees={setNbCommandesAffichees}
      handleTabChange={handleTabChange}
      setEditingCommande={setEditingCommande}
      setShowCommandeForm={setShowCommandeForm}
      genererBonLivraison={genererBonLivraison}
      genererFacture={genererFacture}
      genererAvoir={genererAvoir}
    />
  );

  const renderClients = () => (
    <ClientsList
      filteredClients={getFilteredClients()}
      searchTermClients={searchTermClients}
      setSearchTermClients={setSearchTermClients}
      clientTypeFilter={clientTypeFilter}
      setClientTypeFilter={setClientTypeFilter}
      clientCaFilter={clientCaFilter}
      setClientCaFilter={setClientCaFilter}
      currentPage={currentPageClients}
      setCurrentPage={setCurrentPageClients}
      showClientForm={showClientForm}
      setShowClientForm={setShowClientForm}
      editingClient={editingClient}
      setEditingClient={setEditingClient}
      expandedRows={expandedRows}
      toggleRowExpansion={toggleRowExpansion}
      handleClientSubmit={handleClientSubmit}
      deleteClient={deleteClient}
      openClientDetail={openClientDetail}
      getClientCA={getClientCA}
      getDisplayClientType={getDisplayClientType}
      Pagination={Pagination}
    />
  );

  const renderProduits = () => (
    <ProductsList
      produits={produits}
      filteredProduits={getFilteredProduits()}
      stockAlerts={stockAlerts}
      stockMovements={stockMovements}
      gestionStockActive={gestionStockActive}
      settings={settings}
      searchTermProduits={searchTermProduits}
      setSearchTermProduits={setSearchTermProduits}
      showFinDeSaison={showFinDeSaison}
      setShowFinDeSaison={setShowFinDeSaison}
      familleFilter={produitFamilleFilter}
      setFamilleFilter={setProduitFamilleFilter}
      currentPage={currentPageProduits}
      setCurrentPage={setCurrentPageProduits}
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      showProduitForm={showProduitForm}
      setShowProduitForm={setShowProduitForm}
      showStockForm={showStockForm}
      setShowStockForm={setShowStockForm}
      showStockHistory={showStockHistory}
      setShowStockHistory={setShowStockHistory}
      editingProduit={editingProduit}
      setEditingProduit={setEditingProduit}
      expandedRows={expandedRows}
      toggleRowExpansion={toggleRowExpansion}
      hasWriteAccess={hasWriteAccess}
      handleProduitSubmit={handleProduitSubmit}
      handleStockSubmit={handleStockSubmit}
      deleteProduit={deleteProduit}
      toggleFinDeSaison={toggleFinDeSaison}
      loadStockMovements={loadStockMovements}
      getPaginatedItems={getPaginatedItems}
      Pagination={Pagination}
    />
  );


  const appContextValue = {
    // Auth / rôles
    currentUser,
    isAdmin,
    isComptable,
    hasWriteAccess,
    // Données globales
    settings,
    clients,
    produits,
    commandes,
    // Helpers réseau / UX
    apiCall,
    showNotification,
    // Pagination commune
    getPaginatedItems,
    itemsPerPage,
    setItemsPerPage,
  };

  return (
    <AppProvider value={appContextValue}>
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            {settings?.logo_url ? (
              <div className="header-logo">
                <img src={settings.logo_url} alt={settings?.nom_entreprise || 'Ferme'} className="logo-img" />
                <p>Gestion Maraîcher - {settings?.adresse || 'Adresse'}</p>
              </div>
            ) : (
              <>
                <h1>🌱 {settings?.nom_entreprise || 'Ferme'}</h1>
                <p>Gestion Maraîcher - {settings?.adresse || 'Adresse'}</p>
              </>
            )}
          </div>
          <div className="header-actions">
            <button 
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              ☰
            </button>
            
            {/* Bouton Aide / Relancer onboarding */}
            <button 
              onClick={handleRestartOnboarding}
              className="header-btn onboarding-help-btn"
              title="Relancer la visite guidée"
            >
              ❓
            </button>
            
            {/* Bouton Réglages (admin uniquement) */}
            {isAdmin() && (
              <button 
                onClick={() => handleTabChange('reglages')}
                className="header-btn"
                title="Réglages"
              >
                ⚙️ <span className="header-btn-label">Réglages</span>
              </button>
            )}
            <button onClick={handleLogout} className="header-btn" title="Déconnexion">
              🚪 <span className="header-btn-label">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="nav-container">
        <nav className={`nav-tabs ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <button 
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => handleTabChange('dashboard')}
        >
          Tableau de bord
        </button>
        <button 
          className={activeTab === 'clients' ? 'active' : ''}
          onClick={() => handleTabChange('clients')}
        >
          Clients
        </button>
        <button 
          className={activeTab === 'produits' ? 'active' : ''}
          onClick={() => handleTabChange('produits')}
        >
          Produits
        </button>
        <button 
          className={activeTab === 'commandes' ? 'active' : ''}
          onClick={() => handleTabChange('commandes')}
        >
          Commandes
        </button>
        <button 
          className={activeTab === 'comptabilite' ? 'active' : ''}
          onClick={() => handleTabChange('comptabilite')}
        >
          Comptabilité
        </button>
        {isAdmin() && (
          <button 
            className={activeTab === 'statistiques' ? 'active' : ''}
            onClick={() => handleTabChange('statistiques')}
          >
            Statistiques
          </button>
        )}
        {isAdmin() && (
          <button 
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => handleTabChange('users')}
          >
            Utilisateurs
          </button>
        )}
        {isComptable() && (
          <button 
            className={activeTab === 'reglages' ? 'active' : ''}
            onClick={() => handleTabChange('reglages')}
          >
            Export
          </button>
        )}
      </nav>
      </div>

      <main className="main-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'clients' && renderClients()}
        {activeTab === 'produits' && renderProduits()}
        {activeTab === 'commandes' && (
          <CommandesList
            showCommandeForm={showCommandeForm}
            setShowCommandeForm={setShowCommandeForm}
            editingCommande={editingCommande}
            setEditingCommande={setEditingCommande}
            handleCommandeSubmit={handleCommandeSubmit}
            openCommandeForm={openCommandeForm}
            closeCommandeForm={closeCommandeForm}
            deleteCommande={deleteCommande}
            genererBonLivraison={genererBonLivraison}
            genererFacture={genererFacture}
            genererAvoir={genererAvoir}
            genererEtiquettes={genererEtiquettes}
            canDeleteCommande={canDeleteCommande}
            updatePrixDefaut={updatePrixDefaut}
            getDisplayClientType={getDisplayClientType}
            getFilteredCommandes={getFilteredCommandes}
            lignesProduits={lignesProduits}
            setLignesProduits={setLignesProduits}
            ajouterLigneProduit={ajouterLigneProduit}
            supprimerLigneProduit={supprimerLigneProduit}
            updateLigneProduit={updateLigneProduit}
            commandeSearchTerm={commandeSearchTerm}
            setCommandeSearchTerm={setCommandeSearchTerm}
            commandeClientFilter={commandeClientFilter}
            setCommandeClientFilter={setCommandeClientFilter}
            expandedRows={expandedRows}
            toggleRowExpansion={toggleRowExpansion}
            updateCommandeStatut={updateCommandeStatut}
            saveBrouillon={saveBrouillon}
            currentPageCommandes={currentPageCommandes}
            setCurrentPageCommandes={setCurrentPageCommandes}
            Pagination={Pagination}
          />
        )}
        {activeTab === 'comptabilite' && <Comptabilite />}
        {activeTab === 'statistiques' && (
          <Statistiques
            statistiques={statistiques}
            statistiquesProduits={statistiquesProduits}
            statistiquesOrigines={statistiquesOrigines}
          />
        )}
        {activeTab === 'reglages' && (
          <Reglages
            handleSettingsSubmit={handleSettingsSubmit}
            handleExportComptable={handleExportComptable}
            handleLogoUpload={handleLogoUpload}
            activeSettingsTab={activeSettingsTab}
            setActiveSettingsTab={setActiveSettingsTab}
            exportType={exportType}
            setExportType={setExportType}
          />
        )}
        {activeTab === 'users' && currentUser?.role === 'admin' && (
          <UsersList
            users={users}
            showUserForm={showUserForm}
            setShowUserForm={setShowUserForm}
            handleUserSubmit={handleUserSubmit}
            resetUserPassword={resetUserPassword}
            changeUserRole={changeUserRole}
            toggleUserActive={toggleUserActive}
            deleteUser={deleteUser}
            currentPageUsers={currentPageUsers}
            setCurrentPageUsers={setCurrentPageUsers}
          />
        )}
      </main>

      {/* Zone de notifications toast */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onRemove={removeNotification}
          />
        ))}
      </div>

      {/* Pied de page */}
      <footer className="footer">
        <p>
          Made with ❤️ by <a href="https://www.youdemus.fr" target="_blank" rel="noopener noreferrer">Youdemus</a>
          {' | '}
          <a href="https://www.maraichpro.com" target="_blank" rel="noopener noreferrer">Support & Documentation</a>
        </p>
      </footer>

      {/* NOUVEAU: Bouton Back to Top */}
      {showBackToTop && (
        <button 
          className="back-to-top visible"
          onClick={scrollToTop}
          title="Retour en haut"
        >
          ↑
        </button>
      )}

      {/* NOUVEAU: Modal PDF pour mobiles/iPad */}
      {showMobilePDF && (
        <div className="mobile-pdf-modal">
          <div className="mobile-pdf-header">
            <h3>{mobilePDFTitle}</h3>
            <div className="mobile-pdf-actions">
              <button 
                className="mobile-pdf-print"
                onClick={printMobilePDF}
              >
                🖨️ Imprimer
              </button>
              <button 
                className="mobile-pdf-close"
                onClick={() => {
                  // Libérer la blob URL si applicable pour éviter les fuites mémoire
                  if (mobilePDFIsBlobUrl && mobilePDFContent) {
                    try { URL.revokeObjectURL(mobilePDFContent); } catch (_) { /* noop */ }
                  }
                  setShowMobilePDF(false);
                  setMobilePDFContent('');
                  setMobilePDFIsBlobUrl(false);
                }}
              >
                ✕ Fermer
              </button>
            </div>
          </div>
          <div className="mobile-pdf-content">
            {mobilePDFIsBlobUrl ? (
              <iframe
                ref={mobilePDFIframeRef}
                src={mobilePDFContent}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={mobilePDFTitle}
              />
            ) : (
              <iframe
                ref={mobilePDFIframeRef}
                srcDoc={mobilePDFContent}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={mobilePDFTitle}
              />
            )}
          </div>
        </div>
      )}

      {/* Modal de mot de passe réinitialisé */}
      {showPasswordResetModal && resetPasswordData && (
        <div className="modal-overlay">
          <div className="modal modal-small">
            <div className="modal-header">
              <h3>🔑 Mot de Passe Réinitialisé</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowPasswordResetModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="password-reset-info">
                <p><strong>Utilisateur :</strong> {resetPasswordData.username}</p>
                <p><strong>Nouveau mot de passe :</strong></p>
                <div className="password-display">
                  <code className="password-code">{resetPasswordData.newPassword}</code>
                  <button 
                    onClick={copyPasswordToClipboard}
                    className="btn btn-primary btn-copy"
                    title="Copier le mot de passe"
                  >
                    📋 Copier
                  </button>
                </div>
                <div className="password-warning">
                  ⚠️ <strong>Important :</strong> Transmettez ce mot de passe à l'utilisateur de manière sécurisée. 
                  Il devra le changer lors de sa prochaine connexion.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => setShowPasswordResetModal(false)}
                className="btn btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détail client avec historique */}
      {showClientDetail && selectedClientDetail && (
        <div className="modal-overlay" onClick={closeClientDetail}>
          <div className="modal modal-extra-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Détail Client - {selectedClientDetail.nom_entreprise}</h3>
              <button className="modal-close" onClick={closeClientDetail}>×</button>
            </div>
            
            <div className="client-detail-content">
              {/* Informations du client */}
              <div className="client-info-section">
                <h4 className="section-title">Informations du Client</h4>
                <div className="client-info-grid">
                  <div className="info-item">
                    <label>Entreprise :</label>
                    <span>{selectedClientDetail.nom_entreprise}</span>
                  </div>
                  <div className="info-item">
                    <label>SIRET :</label>
                    <span>{selectedClientDetail.siret}</span>
                  </div>
                  <div className="info-item">
                    <label>Type :</label>
                    <span className={`type-badge ${selectedClientDetail.type_client}`}>
                      {getDisplayClientType(selectedClientDetail.type_client)}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Adresse :</label>
                    <span>{selectedClientDetail.adresse}</span>
                  </div>
                  <div className="info-item">
                    <label>Téléphone :</label>
                    <span>{selectedClientDetail.telephone}</span>
                  </div>
                  <div className="info-item">
                    <label>Email :</label>
                    <span>{selectedClientDetail.email}</span>
                  </div>
                  <div className="info-item">
                    <label>CA Total :</label>
                    <span className="ca-amount">
                      <strong>{formatCurrency(selectedClientDetail.ca_total || getClientCA(selectedClientDetail.id), settings?.devise)}</strong>
                    </span>
                  </div>
                </div>
              </div>

              {loadingClientHistory && (
                <div className="loading-message">
                  Chargement de l'historique...
                </div>
              )}

              {!loadingClientHistory && (
                <>
                  {/* Statistiques par produit */}
                  {clientProductStats.length > 0 && (
                    <div className="product-stats-section">
                      <h4 className="section-title">Volume de Vente par Produit</h4>
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Quantité Totale</th>
                              <th>Nb Commandes</th>
                              <th>Chiffre d'Affaires</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientProductStats.map((stat, index) => (
                              <tr key={index}>
                                <td>{stat.nom_produit}</td>
                                <td>{formatNumber(stat.quantite_totale)} {stat.unite}</td>
                                <td>{stat.nombre_commandes}</td>
                                <td><strong>{formatCurrency(stat.chiffre_affaires, settings?.devise)}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Historique des commandes */}
                  <div className="order-history-section">
                    <h4 className="section-title">
                      Historique des Commandes ({clientOrderHistory.length})
                    </h4>
                    
                    {clientOrderHistory.length === 0 ? (
                      <div className="no-data-message">
                        Aucune commande trouvée pour ce client.
                      </div>
                    ) : (
                      <div className="table-container">
                        <table>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>N° Facture</th>
                              <th>Statut</th>
                              <th>Produits</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientOrderHistory.map(order => (
                              <tr key={order.id}>
                                <td>
                                  {order.date_commande ? 
                                    new Date(order.date_commande).toLocaleDateString('fr-FR') : 
                                    'Non définie'
                                  }
                                </td>
                                <td>{order.numero_facture || 'N/A'}</td>
                                <td>
                                  <span className={`status ${order.statut}`}>
                                    {order.statut}
                                  </span>
                                </td>
                                <td>
                                  <div className="produits-list-compact">
                                    {order.produits && order.produits.length > 0 ? 
                                      order.produits.map((p, index) => (
                                        <span key={index} className="produit-compact">
                                          {p.nom_produit} ({formatNumber(p.quantite)} {p.unite})
                                        </span>
                                      )) : 
                                      <span className="no-products">Aucun produit</span>
                                    }
                                  </div>
                                </td>
                                <td>
                                  <strong>{formatCurrency(order.total, settings?.devise)}</strong>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de changement de mot de passe obligatoire */}
      {showPasswordChangeModal && (
        <div className="modal-overlay" style={{zIndex: 9999}}>
          <div className="modal">
            <div className="modal-header">
              <h3>🔐 Changement de mot de passe obligatoire</h3>
            </div>
            <div className="modal-content">
              <p className="password-change-notice">
                Pour des raisons de sécurité, vous devez changer le mot de passe par défaut 
                lors de votre première connexion.
              </p>
              
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Mot de passe actuel *</label>
                  <input 
                    type="password" 
                    name="current_password" 
                    required 
                    placeholder="Votre mot de passe actuel"
                  />
                </div>
                
                <div className="form-group">
                  <label>Nouveau mot de passe *</label>
                  <input 
                    type="password" 
                    name="new_password" 
                    required 
                    minLength="8"
                    placeholder="Minimum 8 caractères"
                  />
                </div>
                
                <div className="form-group">
                  <label>Confirmer le nouveau mot de passe *</label>
                  <input 
                    type="password" 
                    name="confirm_password" 
                    required 
                    minLength="8"
                    placeholder="Confirmer le nouveau mot de passe"
                  />
                </div>
                
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Changer le mot de passe
                  </button>
                </div>
                
                <div className="password-requirements">
                  <small>
                    <strong>Exigences :</strong><br/>
                    • Minimum 8 caractères<br/>
                    • Différent du mot de passe actuel
                  </small>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && currentUser && (
        <Onboarding 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          currentUser={currentUser}
          primaryColor={settings?.couleur_primaire || '#8CC63F'}
        />
      )}
    </div>
    </AppProvider>
  );
}

export default App;
